import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PdUIKitProvider } from '@pd/pd-ui-kit';
import { useTranslation } from 'react-i18next';

import IconFilter from '~/components/Icons/icon-filter';
import Button from '~/shared/ui/Fields/Button';
import {
  type FilterField as FilterFieldI,
  type FilterValues,
} from '~/features/filter/model/types';
import { useFilterForm } from '~/features/filter/model/useFilterForm';
import { useUrlFilters } from '~/features/filter/model/useUrlFilters';

import { FilterActions } from './FilterActions';
import { FilterField } from './FilterField';

import cls from './FilterRoot.module.less';

interface FilterRootProps {
  className?: string;
  alwaysShow?: boolean;
  fields: FilterFieldI[];
  /**
   * Минимальное количество колонок грида.
   * По умолчанию 5. Если полей больше — колонки автоматически увеличиваются,
   * чтобы поля укладывались в заданное количество строк (см. `rows`).
   */
  columns?: number;
  /**
   * Желаемое количество строк. Колонки будут увеличены так, чтобы поля
   * распределились равномерно в это число строк. Не может уменьшить сетку
   * ниже `columns`.
   */
  rows?: number;
  /** Доп. колбек после применения фильтра. */
  onSubmit?: (values: FilterValues) => void;
  /** Доп. колбек после сброса. */
  onReset?: () => void;
  /**
   * Скрыть кнопки «Показать»/«Сбросить». В этом режиме изменения полей
   * сразу применяются в URL, а сброс недоступен.
   */
  hideActions?: boolean;
}

const DEFAULT_GRID_COLUMNS = 5;

export const FilterRoot = (props: FilterRootProps) => {
  const {
    className,
    alwaysShow = false,
    fields,
    columns = DEFAULT_GRID_COLUMNS,
    rows,
    onSubmit: onSubmitExternal,
    onReset,
    hideActions = false,
  } = props;

  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState<boolean>(alwaysShow);

  const { defaultValues, applyFilters, resetFilters } = useUrlFilters(fields);
  const { control, handleSubmit, reset, watch, setValue } = useFilterForm<FilterValues>(defaultValues);

  // Синхронизируем форму при изменениях URL (внешние навигации, back/forward).
  useEffect(() => {
    reset(defaultValues);
    // reset стабилен, а defaultValues пересчитывается на каждый рендер,
    // поэтому стабилизируем зависимостью по сериализованному снимку.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(defaultValues)]);

  // Снимок всех значений формы. В RHF v6 watch без аргументов подписывает
  // компонент на изменения полей и возвращает текущие значения
  // (callback-подписки, как в v7, в v6 нет).
  const watchedValues = watch();
  const prevWatchedRef = useRef<FilterValues>(watchedValues);

  // Без кнопок действий применяем фильтры сразу при изменении поля.
  // Сравнение с предыдущим снимком отсекает программный reset из эффекта
  // выше (после записи в URL значения формы и снимок совпадают),
  // поэтому запись в URL не зацикливается.
  useEffect(() => {
    if (!hideActions) return;
    const prev = prevWatchedRef.current;
    prevWatchedRef.current = watchedValues;
    if (JSON.stringify(prev) !== JSON.stringify(watchedValues)) {
      applyFilters(watchedValues);
    }
  }, [hideActions, JSON.stringify(watchedValues)]);

  const onToggleFilter = () => setIsOpen((s) => !s);

  const closeFilter = () => {
    if (!alwaysShow) {
      setIsOpen(false);
    }
  }

  const onResetClick = () => {
    // Важно: используем `null`, а не `undefined`.
    // react-hook-form v6 при reset() очищает fieldsRef и Controllers
    // пере-регистрируются. В момент повторной регистрации RHF применяет
    // значение из defaultValues только если оно НЕ undefined, иначе
    // внутренний `valueRef.current` Controller-а остаётся со старым значением,
    // и при следующем submit ранее заполненные и «сброшенные» поля снова
    // попадают в форму. С `null` RHF вызовет setFieldValue и корректно
    // очистит и React-state, и valueRef.
    const empty: FilterValues = fields.reduce<FilterValues>((acc, f) => {
      acc[f.name] = null;
      if (f.nameTo) acc[f.nameTo] = null;
      return acc;
    }, {});
    reset(empty);
    resetFilters();
    onReset?.();
    closeFilter();
  };

  const onSubmit = (values: FilterValues) => {
    applyFilters(values);
    onSubmitExternal?.(values);
    closeFilter();
  };

  const gridColumns = useMemo(() => {
    let maxSpan = 0;
    let totalSpan = 0;
    for (const field of fields) {
      const span = field.span || 1;
      totalSpan += span;
      if (span > maxSpan) maxSpan = span;
    }
    const byRows = rows && rows > 0 ? Math.ceil(totalSpan / rows) : 0;
    return Math.max(columns, maxSpan, byRows);
  }, [fields, columns, rows]);

  const fieldRowChunks = useMemo(() => {
    const chunks: FilterFieldI[][] = [];
    let currentChunk: FilterFieldI[] = [];
    let currentWidth = 0;

    for (const field of fields) {
      const fieldWidth = field.span || 1;

      const isOverflow = currentWidth + fieldWidth > gridColumns;
      const isForcedBreak = Boolean(field.newRow) && currentChunk.length > 0;

      if ((isOverflow || isForcedBreak) && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentWidth = 0;
      }

      currentChunk.push(field);
      currentWidth += fieldWidth;
    }

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }

    return chunks;
  }, [fields, gridColumns]);

  // TODO: Для нового дизайна, в будущем потребуется убрать button показа/скрытия фильтра, он всегда должен быть отображен
  return (
    <div className={clsx(cls.FilterRoot, { [cls.FilterOpen]: alwaysShow }, className)}>
      {!alwaysShow && (
        <Button
          txtBtn={isOpen ? t('ui.hide') : t('ui.filter')}
          onClick={onToggleFilter}
          small
        >
          <IconFilter />
        </Button>
      )}
      {isOpen && (
        <div className={cls.FilterForm}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {fieldRowChunks.map((chunk, i) => {
              // Строка из 3+ полей растягивается на всю ширину фильтра
              // (колонки по сумме span строки). Строка из 1-2 полей
              // остаётся в общей сетке, чтобы поля не расползались.
              const isWideRow = chunk.length > 2;
              const chunkWidth = isWideRow
                ? chunk.reduce((sum, field) => sum + (field.span || 1), 0)
                : gridColumns;

              // Если в строке есть поля с fixedWidth — строим шаблон строки
              // пошаблонно: фиксированные поля в px, остальные делят
              // оставшееся место пропорционально span (как fr-множитель).
              const hasFixedWidth = chunk.some((field) => field.fixedWidth);
              const gridTemplateColumns = hasFixedWidth
                ? chunk
                    .map((field) =>
                      field.fixedWidth
                        ? `${field.fixedWidth}px`
                        : `minmax(0, ${field.span || 1}fr)`
                    )
                    .join(" ")
                : `repeat(${chunkWidth}, minmax(0, 1fr))`;

              return (
               <ul key={i} className={cls.FilterFields} style={{ gridTemplateColumns }}>
                 {chunk.map((field) => (
                   <li className={cls.FilterField} key={field.name} style={{ gridColumn: !hasFixedWidth && field.span ? `span ${field.span}` : undefined }}>
                     <label>{field.label}</label>
                     <FilterField field={field} control={control} setValue={setValue} />
                   </li>
                 ))}
               </ul>
              );
            })}

            {!hideActions && <FilterActions resetForm={onResetClick} />}
          </form>
        </div>
      )}
    </div>
  );
};
